import { useEffect, useRef, useState } from 'react';
import { ApiError, artApi, introdbApi, metaApi, type ArtConfig, type Provider } from '../api';
import { artSource, clearArtCaches, setArtSource, type ArtSource } from '../meta';
import { linkProps } from '../router';

type Status = Record<Provider, { configured: boolean; ok: boolean | null; message: string | null }>;

const FIELDS: { key: Provider; label: string; help: string; secret?: boolean }[] = [
  { key: 'tmdb', label: 'TMDB', help: 'API-sleutel (v3) of Read Access Token (v4). themoviedb.org → Instellingen → API.' },
  { key: 'fanart', label: 'Fanart.tv', help: 'Persoonlijke API-sleutel. fanart.tv → profiel → API.' },
  { key: 'mdblist', label: 'MDBList', help: 'API-sleutel voor scores (IMDb, RT, Metacritic, Letterboxd) en lijsten. mdblist.com → Preferences.' },
  { key: 'traktClientId', label: 'Trakt Client ID', help: 'Maak een app op trakt.tv/oauth/applications (Production, Redirect URI: urn:ietf:wg:oauth:2.0:oob). Meer is niet nodig.' },
  { key: 'simklClientId', label: 'SIMKL Client ID', help: 'Maak een app op simkl.com/settings/developer (Redirect URI: urn:ietf:wg:oauth:2.0:oob). Voor trending (ook anime) en je SIMKL-lijsten.' },
  { key: 'opensubtitles', label: 'OpenSubtitles API-sleutel', help: 'Voor ondertitels bij bronnen uit addons. opensubtitles.com → profiel → API consumers → nieuwe consumer.' },
  { key: 'opensubtitlesUser', label: 'OpenSubtitles gebruikersnaam (optioneel)', help: 'Met je (gratis) account mag je meer ondertitels per dag downloaden dan zonder.' },
  { key: 'opensubtitlesPass', label: 'OpenSubtitles wachtwoord (optioneel)', help: 'Wordt versleuteld op je server bewaard en is hierna niet meer zichtbaar.', secret: true },
  { key: 'traktClientSecret', label: 'Trakt Client Secret (optioneel)', help: 'Alleen voor oudere Trakt-apps. Nieuwe apps krijgen geen secret meer: laat dit leeg.', secret: true },
];

/** Koppelingen met externe bronnen (eigenaar), Trakt-koppeling (iedereen) en posterbron (per browser). */
export type ConnectionPart = 'keys' | 'trakt' | 'home' | 'artwork';

export function Connections({ parts = ['keys', 'trakt', 'home', 'artwork'] }: { parts?: ConnectionPart[] }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [values, setValues] = useState<Partial<Record<Provider, string>>>({});
  const [results, setResults] = useState<Record<string, { ok: boolean; message?: string }>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    metaApi.providers().then((r) => { setStatus(r.providers); setCanManage(r.canManage); }).catch(() => setStatus(null));
  }, []);

  const save = async (only?: Provider, clear = false) => {
    setBusy(true);
    try {
      const body: Partial<Record<Provider, string | null>> = {};
      for (const f of FIELDS) {
        if (only && f.key !== only) continue;
        if (clear) body[f.key] = null;
        else if (values[f.key]) body[f.key] = values[f.key]!;
      }
      const r = await metaApi.saveProviders(body);
      setStatus(r.providers);
      setResults((x) => ({ ...x, ...r.results }));
      setValues((v) => {
        const n = { ...v };
        for (const k of Object.keys(r.results)) if (r.results[k].ok) delete n[k as Provider];
        return n;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {parts.includes('keys') && canManage && status && (
        <form className="settings-block form-grid" onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <h2>Koppelingen</h2>
          <p className="subtle hint">
            Voor clearlogo's, achtergronden, scores, trailers en (straks) widgets zoals Top 10 en Trending.
            Alleen jij ziet dit; de sleutels worden versleuteld op je server bewaard en zijn hierna niet meer zichtbaar.
          </p>
          {FIELDS.map((f) => {
            const st = status[f.key];
            const res = results[f.key];
            return (
              <div key={f.key} className="conn-row">
                <label htmlFor={`k-${f.key}`}>
                  {f.label}
                  <span className={`status-pill ${st.configured && st.ok !== false ? 'ok' : st.configured ? 'warn' : ''}`}>
                    {st.configured ? (st.ok === false ? 'Fout' : 'Ingesteld') : 'Niet ingesteld'}
                  </span>
                </label>
                <div className="inline-form">
                  <input
                    id={`k-${f.key}`}
                    className="text-input"
                    type="password"
                    autoComplete="off"
                    value={values[f.key] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    placeholder={st.configured ? 'Opgeslagen — alleen invullen om te wijzigen' : ''}
                  />
                  {st.configured && <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void save(f.key, true)}>Verwijderen</button>}
                </div>
                <span className="subtle hint">{f.help}</span>
                {res && <span className={res.ok ? 'subtle' : 'error'} role="status">{res.ok ? (res.message ?? 'Werkt') : res.message}</span>}
              </div>
            );
          })}
          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={busy || !Object.values(values).some(Boolean)}>{busy ? 'Testen…' : 'Testen en opslaan'}</button>
          </div>
        </form>
      )}
      {parts.includes('trakt') && <TraktLink />}
      {parts.includes('trakt') && <SimklLink />}
      {parts.includes('trakt') && <IntroDbKey />}
      {parts.includes('home') && <section className="settings-block" aria-label="Startscherm">
        <h2>Startscherm</h2>
        <p className="subtle hint">Hubs en widgets (Top 10, Trending, je bibliotheek, Live sport…) toevoegen en ordenen, voor iedereen of alleen dit profiel.</p>
        <div className="form-actions"><a className="btn btn-primary" {...linkProps('/settings/home')}>Startscherm aanpassen</a></div>
      </section>}
      {parts.includes('artwork') && <ArtworkChoice />}
    </>
  );
}

export function TraktLink({ onLinked }: { onLinked?: () => void } = {}) {
  const [st, setSt] = useState<{ configured: boolean; linked: boolean; username: string | null } | null>(null);
  const [device, setDevice] = useState<{ deviceCode: string; userCode: string; verificationUrl: string; interval: number } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    metaApi.trakt().then(setSt).catch(() => setSt(null));
    return () => window.clearTimeout(timer.current);
  }, []);

  const start = async () => {
    setMsg(null);
    try {
      const d = await metaApi.traktDevice();
      setDevice(d);
      const poll = async () => {
        try {
          const r = await metaApi.traktPoll(d.deviceCode);
          if (r.status === 'ok') {
            setDevice(null);
            setSt((s) => (s ? { ...s, linked: true, username: r.username } : s));
            onLinked?.();
            return;
          }
          if (r.status === 'expired' || r.status === 'denied') {
            setDevice(null);
            setMsg(r.status === 'denied' ? 'Geweigerd op trakt.tv.' : 'De code is verlopen. Probeer opnieuw.');
            return;
          }
        } catch {
          // even geen verbinding: gewoon opnieuw
        }
        timer.current = window.setTimeout(poll, Math.max(5, d.interval) * 1000);
      };
      timer.current = window.setTimeout(poll, Math.max(5, d.interval) * 1000);
    } catch (err) {
      setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Trakt koppelen lukt nu niet.');
    }
  };

  if (!st?.configured) return null;
  return (
    <section className="settings-block" aria-label="Trakt">
      <div className="account-head">
        <h2>Trakt</h2>
        <span className={`status-pill ${st.linked ? 'ok' : ''}`}>{st.linked ? `Gekoppeld${st.username ? ` als ${st.username}` : ''}` : 'Niet gekoppeld'}</span>
      </div>
      <p className="subtle hint">Koppel je eigen Trakt-account voor je watchlist, aanbevelingen en het bijhouden van wat je kijkt. Geldt voor je Plex-account.</p>
      {device ? (
        <div className="trakt-code">
          <span>Ga naar <a href={device.verificationUrl} target="_blank" rel="noreferrer"><strong>{device.verificationUrl.replace(/^https?:\/\//, '')}</strong></a> en voer deze code in:</span>
          <span className="pin-code" style={{ fontSize: 'calc(var(--u) * 3.6)' }}>{device.userCode}</span>
          <span className="subtle">Wachten op bevestiging…</span>
        </div>
      ) : (
        <div className="form-actions">
          {st.linked ? (
            <button className="btn btn-ghost" onClick={async () => { await metaApi.traktUnlink().catch(() => undefined); setSt({ ...st, linked: false, username: null }); }}>Ontkoppelen</button>
          ) : (
            <button className="btn btn-primary" onClick={() => void start()}>Trakt koppelen</button>
          )}
        </div>
      )}
      {msg && <p className="error" role="alert">{msg}</p>}
    </section>
  );
}

export function SimklLink({ onLinked }: { onLinked?: () => void } = {}) {
  const [st, setSt] = useState<{ configured: boolean; linked: boolean; username: string | null } | null>(null);
  const [device, setDevice] = useState<{ deviceCode: string; userCode: string; verificationUrl: string; interval: number } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    metaApi.simkl().then(setSt).catch(() => setSt(null));
    return () => window.clearTimeout(timer.current);
  }, []);

  const start = async () => {
    setMsg(null);
    try {
      const d = await metaApi.simklDevice();
      setDevice(d);
      const poll = async () => {
        try {
          const r = await metaApi.simklPoll(d.deviceCode);
          if (r.status === 'ok') {
            setDevice(null);
            setSt((s) => (s ? { ...s, linked: true, username: r.username } : s));
            onLinked?.();
            return;
          }
          if (r.status === 'expired' || r.status === 'denied') {
            setDevice(null);
            setMsg(r.status === 'denied' ? 'Geweigerd op simkl.com.' : 'De code is verlopen. Probeer opnieuw.');
            return;
          }
        } catch {
          // even geen verbinding: gewoon opnieuw
        }
        timer.current = window.setTimeout(poll, Math.max(5, d.interval) * 1000);
      };
      timer.current = window.setTimeout(poll, Math.max(5, d.interval) * 1000);
    } catch (err) {
      setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'SIMKL koppelen lukt nu niet.');
    }
  };

  if (!st?.configured) return null;
  return (
    <section className="settings-block" aria-label="SIMKL">
      <div className="account-head">
        <h2>SIMKL</h2>
        <span className={`status-pill ${st.linked ? 'ok' : ''}`}>{st.linked ? `Gekoppeld${st.username ? ` als ${st.username}` : ''}` : 'Niet gekoppeld'}</span>
      </div>
      <p className="subtle hint">Koppel je SIMKL-account voor je eigen lijsten (Plan to watch, Bezig) en het bijhouden van wat je kijkt. Geldt voor je Plex-account.</p>
      {device ? (
        <div className="trakt-code">
          <span>Ga naar <a href={device.verificationUrl} target="_blank" rel="noreferrer"><strong>{device.verificationUrl.replace(/^https?:\/\//, '')}</strong></a> en voer deze code in:</span>
          <span className="pin-code" style={{ fontSize: 'calc(var(--u) * 3.6)' }}>{device.userCode}</span>
          <span className="subtle">Wachten op bevestiging…</span>
        </div>
      ) : (
        <div className="form-actions">
          {st.linked ? (
            <button className="btn btn-ghost" onClick={async () => { await metaApi.simklUnlink().catch(() => undefined); setSt({ ...st, linked: false, username: null }); }}>Ontkoppelen</button>
          ) : (
            <button className="btn btn-primary" onClick={() => void start()}>SIMKL koppelen</button>
          )}
        </div>
      )}
      {msg && <p className="error" role="alert">{msg}</p>}
    </section>
  );
}

function ArtworkChoice() {
  const [src, setSrc] = useState<ArtSource>(artSource());
  const [cfg, setCfg] = useState<ArtConfig | null>(null);
  useEffect(() => {
    artApi.config().then(setCfg).catch(() => setCfg(null));
  }, []);
  const options: { id: ArtSource; label: string; help: string }[] = [
    { id: 'plex', label: 'Plex', help: 'Posters uit Plex, inclusief die van Overlayrr en wat de beheerder vastzette' },
    { id: 'tmdb', label: 'TMDB', help: 'Officiële posters, schoon en consistent' },
    { id: 'fanart', label: 'Fanart.tv', help: 'Alternatieve posters en achtergronden; per onderdeel TMDB als er niets is' },
    ...(cfg?.spatial ? [{ id: 'spatial' as ArtSource, label: 'SpatialPosters', help: cfg.spatialLabels ? 'Posters met logo en labels van SpatialPosters' : 'Posters met logo van SpatialPosters; labels van tumedia' }] : []),
  ];
  return (
    <section className="settings-block" aria-label="Artwork">
      <h2>Artwork</h2>
      <p className="subtle hint">Waar posters en achtergronden in rijen vandaan komen. Geldt voor deze browser; ververs de pagina na wijzigen.</p>
      <div className="theme-grid" role="radiogroup" aria-label="Posterbron" data-focus-group>
        {options.map((o) => (
          <button key={o.id} role="radio" aria-checked={src === o.id} className="theme-option art-option" onClick={() => { setArtSource(o.id); setSrc(o.id); }}>
            <span className="theme-name">{o.label}</span>
            <span className="theme-meta">{o.help}</span>
          </button>
        ))}
      </div>
      {cfg?.canManage && <ArtworkAdmin cfg={cfg} onChange={setCfg} />}
    </section>
  );
}

/** Alleen de beheerder: taalvoorkeur en SpatialPosters (gelden voor iedereen). */
function ArtworkAdmin({ cfg, onChange }: { cfg: ArtConfig; onChange: (c: ArtConfig) => void }) {
  const [url, setUrl] = useState(cfg.spatialUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const save = async (b: Parameters<typeof artApi.saveConfig>[0], ok?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      onChange(await artApi.saveConfig(b));
      clearArtCaches();
      if (ok) setMsg(ok);
    } catch (e) {
      setMsg(e instanceof ApiError && e.message !== e.code ? e.message : 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="art-admin">
      <h3>Beheer (voor iedereen)</h3>
      <div className="form-grid">
        <label className="ed-field">Posters: taal
          <select value={cfg.posterLang} disabled={busy} onChange={(e) => void save({ posterLang: e.target.value as ArtConfig['posterLang'] })}>
            <option value="nl">Nederlands, dan Engels, dan zonder tekst</option>
            <option value="en">Engels, dan Nederlands, dan zonder tekst</option>
            <option value="textless">Liefst zonder tekst</option>
          </select>
        </label>
        <label className="ed-field">Logo's: taal
          <select value={cfg.logoLang} disabled={busy} onChange={(e) => void save({ logoLang: e.target.value as ArtConfig['logoLang'] })}>
            <option value="nl">Nederlands eerst</option>
            <option value="en">Engels eerst</option>
          </select>
        </label>
      </div>
      <p className="subtle hint">Binnen dezelfde taal wint de afbeelding met de meeste likes (Fanart.tv) of stemmen (TMDB). Per titel kun je ook zelf kiezen met "Artwork kiezen" op de titelpagina.</p>

      <h3>SpatialPosters</h3>
      <p className="subtle hint">Eigen SpatialPosters-container (Docker) die kant-en-klare posters maakt. tumedia haalt de posters op en stuurt ze door, dus de container hoeft alleen voor de server bereikbaar te zijn. Achtergronden en logo's blijven van Fanart.tv/TMDB.</p>
      <div className="inline-form">
        <label className="visually-hidden" htmlFor="spatial-url">Adres van SpatialPosters</label>
        <input id="spatial-url" className="text-input" placeholder="http://192.168.1.3:3000" value={url} onChange={(e) => setUrl(e.target.value)} />
        <button className="btn btn-primary" disabled={busy || !url.trim() || url.trim() === cfg.spatialUrl} onClick={() => void save({ spatialUrl: url.trim() }, 'Gekoppeld en getest')}>{busy ? 'Testen…' : 'Opslaan en testen'}</button>
        {cfg.spatial && <button className="btn btn-ghost" disabled={busy} onClick={() => { setUrl(''); void save({ spatialUrl: null }, 'Ontkoppeld'); }}>Ontkoppelen</button>}
      </div>
      {cfg.spatial && (
        <label className="check-row">
          <input type="checkbox" checked={cfg.spatialLabels} disabled={busy} onChange={(e) => void save({ spatialLabels: e.target.checked })} />
          <span>
            <strong>Labels van SpatialPosters gebruiken</strong>
            <span className="subtle"> Aan: de labels (4K, HDR, beoordelingen) komen op de poster van SpatialPosters en tumedia zet zijn eigen labels uit. Uit: alleen de poster, met de labels van tumedia. Stel in SpatialPosters zelf in welke labels erop komen.</span>
          </span>
        </label>
      )}
      {msg && <p className="subtle" role="status">{msg}</p>}
    </div>
  );
}

/** Eigen IntroDB-sleutel (per Plex-account): intro's en samenvattingen overslaan werkt ook zonder; met sleutel kun je zelf tijden insturen. */
function IntroDbKey() {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [value, setValue] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    introdbApi.status().then((r) => setConfigured(r.configured)).catch(() => setConfigured(false));
  }, []);
  const save = async (key: string | null) => {
    setMsg(null);
    try {
      const r = await introdbApi.save(key);
      setConfigured(r.configured);
      setValue('');
      setMsg(r.configured ? 'Opgeslagen' : 'Verwijderd');
    } catch (e) {
      setMsg(e instanceof ApiError && e.message !== e.code ? e.message : 'Opslaan mislukt');
    }
  };
  return (
    <section className="settings-block" aria-label="IntroDB">
      <div className="account-head">
        <h2>IntroDB</h2>
        <span className={`status-pill ${configured ? 'ok' : ''}`}>{configured ? 'Eigen sleutel ingesteld' : 'Zonder sleutel'}</span>
      </div>
      <p className="subtle hint">
        Intro's, samenvattingen ("previously on") en aftitelingen overslaan, ook als Plex ze zelf niet kent. Dat werkt al zonder sleutel.
        Met je <strong>eigen</strong> sleutel (introdb.app → Account settings) kun je in de player ook zelf tijden markeren en insturen. De sleutel is alleen van jou en wordt versleuteld bewaard.
      </p>
      <form className="inline-form" onSubmit={(e) => { e.preventDefault(); if (value.trim()) void save(value.trim()); }}>
        <input className="text-input" type="password" autoComplete="off" value={value} placeholder={configured ? 'Opgeslagen — alleen invullen om te wijzigen' : 'idb_…'} onChange={(e) => setValue(e.target.value)} />
        <button className="btn btn-primary" disabled={!value.trim()}>Opslaan</button>
        {configured && <button type="button" className="btn btn-ghost" onClick={() => void save(null)}>Verwijderen</button>}
      </form>
      {msg && <span className="subtle" role="status">{msg}</span>}
    </section>
  );
}
